/**
 * React Query Cache Invalidation Utilities
 *
 * Provides consistent cache invalidation patterns across the application.
 * Ensures UI updates immediately after mutations without requiring page refresh.
 */

import { QueryClient } from "@tanstack/react-query";
import { Transaction } from "@/types/financial";
import { clearApiGetResponseCache } from "@/services/assets/assetHelpers";

function bustTransportGetCache(): void {
  clearApiGetResponseCache();
}

/**
 * Invalidate all queries related to a user
 * Use this after any mutation that affects user data
 */
export const invalidateUserQueries = (queryClient: QueryClient, userId: string) => {
  bustTransportGetCache();
  // Invalidate all user-specific queries
  queryClient.invalidateQueries({ queryKey: ["assets", userId] });
  queryClient.invalidateQueries({ queryKey: ["assets"] });
  queryClient.invalidateQueries({ queryKey: ["liabilities", userId] });
  queryClient.invalidateQueries({ queryKey: ["liabilities"] });
  queryClient.invalidateQueries({ queryKey: ["transactions", userId] });
  queryClient.invalidateQueries({ queryKey: ["transactions"] });
  queryClient.invalidateQueries({ queryKey: ["goals", userId] });
  queryClient.invalidateQueries({ queryKey: ["goals"] });
  queryClient.invalidateQueries({ queryKey: ["dashboard", userId] });
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
  queryClient.invalidateQueries({ queryKey: ["notifications"] });
  queryClient.invalidateQueries({ queryKey: ["family", userId] });
  queryClient.invalidateQueries({ queryKey: ["family"] });
  queryClient.invalidateQueries({ queryKey: ["profile", userId] });
  queryClient.invalidateQueries({ queryKey: ["profile"] });
  queryClient.invalidateQueries({ queryKey: ["referrals", userId] });
  queryClient.invalidateQueries({ queryKey: ["referrals"] });
};

/**
 * Refetch all queries related to a user
 * Use this when you need immediate data refresh
 */
export const refetchUserQueries = async (queryClient: QueryClient, userId: string) => {
  bustTransportGetCache();
  // Refetch all user-specific queries
  await Promise.all([
    queryClient.refetchQueries({ queryKey: ["assets", userId] }),
    queryClient.refetchQueries({ queryKey: ["liabilities", userId] }),
    queryClient.refetchQueries({ queryKey: ["transactions", userId] }),
    queryClient.refetchQueries({ queryKey: ["goals", userId] }),
    queryClient.refetchQueries({ queryKey: ["dashboard", userId] }),
  ]);
};

/**
 * Invalidate liability-related queries
 */
export const invalidateLiabilityQueries = (queryClient: QueryClient, userId?: string) => {
  bustTransportGetCache();
  queryClient.invalidateQueries({ queryKey: ["liabilities"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["liabilities", userId] });
  }
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["dashboard", userId] });
  }
};

/**
 * Invalidate asset-related queries
 */
export const invalidateAssetQueries = (queryClient: QueryClient, userId?: string) => {
  bustTransportGetCache();
  queryClient.invalidateQueries({ queryKey: ["assets"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["assets", userId] });
  }
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["dashboard", userId] });
  }
};

/**
 * Invalidate transaction-related queries
 */
export const invalidateTransactionQueries = (queryClient: QueryClient, userId?: string) => {
  bustTransportGetCache();
  queryClient.invalidateQueries({ queryKey: ["transactions"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["transactions", userId] });
  }
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["dashboard", userId] });
  }
};

/**
 * Invalidate goal-related queries
 */
export const invalidateGoalQueries = (queryClient: QueryClient, userId?: string) => {
  bustTransportGetCache();
  queryClient.invalidateQueries({ queryKey: ["goals"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["goals", userId] });
    queryClient.invalidateQueries({ queryKey: ["goals", "contributions", userId] });
  }
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["dashboard", userId] });
  }
};

/**
 * Invalidate family-related queries
 */
export const invalidateFamilyQueries = (queryClient: QueryClient, userId?: string) => {
  queryClient.invalidateQueries({ queryKey: ["family"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["family", userId] });
  }
};

/**
 * Invalidate notification-related queries
 */
export const invalidateNotificationQueries = (queryClient: QueryClient, userId?: string) => {
  queryClient.invalidateQueries({ queryKey: ["notifications"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
  }
};

/**
 * Invalidate SplitMage group list queries (used by SplitMage landing page and Transactions mappings).
 *
 * Note: some callers use query keys like ["splitmage-groups", userId] while others may use
 * ["splitmage-groups", groupIds]. Invalidating the prefix ["splitmage-groups"] will match all.
 */
export const invalidateSplitMageGroupsQueries = (queryClient: QueryClient, userId?: string) => {
  queryClient.invalidateQueries({ queryKey: ["splitmage-groups"] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: ["splitmage-groups", userId] });
  }
};

/**
 * Invalidate SplitMage group-scoped queries so all tabs (expenses, balances, analytics, members)
 * refresh consistently after mutations.
 */
export const invalidateSplitMageGroupQueries = (queryClient: QueryClient, groupId: string) => {
  queryClient.invalidateQueries({ queryKey: ["splitmage-group", groupId] });
  queryClient.invalidateQueries({ queryKey: ["splitmage-expenses", groupId] });
  queryClient.invalidateQueries({ queryKey: ["splitmage-balances", groupId] });
  queryClient.invalidateQueries({ queryKey: ["splitmage-analytics", groupId] });
  queryClient.invalidateQueries({ queryKey: ["splitmage-members", groupId] });
};

/**
 * Optimistically add a newly-created transaction to the React Query cache for immediate UI updates.
 *
 * Important: The Transactions page stores *decrypted/plaintext* descriptions in cache.
 * If you store encrypted text + `encryption_iv` here, the UI will show cipher text.
 */
export const addOptimisticTransactionToCache = (
  queryClient: QueryClient,
  userId: string,
  transaction: Transaction
) => {
  queryClient.setQueryData(["transactions", userId], (old: Transaction[] | undefined) =>
    upsertOptimisticTransactionInCache(old ?? [], transaction)
  );
};

/**
 * Pure helper for deterministic cache updates (easy to test).
 * Upserts by id and moves the item to the front (most recent).
 */
export const upsertOptimisticTransactionInCache = (prev: Transaction[], next: Transaction) => {
  const withoutDuplicate = prev.filter((tx) => tx.id !== next.id);
  return [next, ...withoutDuplicate];
};

/**
 * Pure helper for deterministic cache updates (easy to test).
 */
export const removeOptimisticTransactionFromCache = (
  prev: Transaction[],
  transactionId: string
) => {
  return prev.filter((tx) => tx.id !== transactionId);
};

/**
 * Invalidate admin-related queries
 * Use this after any admin mutation (user create/delete/update, subscription changes, etc.)
 */
export const invalidateAdminQueries = (queryClient: QueryClient) => {
  // Invalidate all admin queries to ensure dashboard, users list, stats, and assets refresh
  queryClient.invalidateQueries({ queryKey: ["admin"] });
  queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
  queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
  queryClient.invalidateQueries({ queryKey: ["admin", "assets"] });
};
